/* eslint-disable react/require-default-props */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import classNames from 'classnames';
import { Typography } from '@one-thd/sui-atomic-components';
import {
  isBasicPerItemPrice,
  isStrikeThroughPerItemPrice,
  isStrikeThroughPrice,
  triggerNewRelicEvent
} from '../../utils/utils';
import { SignInLink } from './subcomponents/signInLink/SignInLink';
import helpers from '../../helpers/price-helper';

export const PriceMinimal = ({
  skuModel,
  quantity,
  align = 'start',
  useNewMapPolicy = false,
  isSignInToolTip = false
}) => {
  const { isCustomerIdentified = false } = useThdCustomer() || {};
  const skuModelProduct = skuModel?._product;
  const skuModelPricing = skuModelProduct?.pricing ?? {};
  const skuModelPromotion = skuModelProduct?.pricing?.promotion ?? {};
  const mapPolicy = skuModelPricing?.mapDetail?.mapPolicy || '';
  const totalPrice = skuModelPricing?.cartItem?.total ?? 0.00;

  useEffect(() => {
    if (skuModelPricing?.itemId) {
      // new relic event for price displayed
      triggerNewRelicEvent({
        actionName: isCustomerIdentified ? 'PriceDisplayedAfterAuth' : 'MapPriceDisplayed',
        layoutType: 'MinimalView',
        subExperience: 'PriceMinimal',
        customerStatus: isCustomerIdentified ? 'authenticated' : 'unauthenticated',
        itemId: skuModelPricing?.itemId,
        isCustomerIdentified // Pass the authentication status
      });
    }
    // new relic event for missing skuModelProduct, skuModelPricing, or mapPolicy
    if (!skuModelProduct && !skuModelPricing && (useNewMapPolicy && !mapPolicy)) {
      triggerNewRelicEvent({
        actionName: 'PriceMinimalError',
        layoutType: 'MinimalView',
        subExperience: 'PriceMinimal',
        customerStatus: isCustomerIdentified ? 'authenticated' : 'unauthenticated',
        itemId: skuModelPricing?.itemId,
        isCustomerIdentified, // Pass the authentication status
        error: (useNewMapPolicy && !mapPolicy) ? 'No MAP Policy detected' : 'No Sku Model Product or Pricing Object',
      });
    }
  }, [isCustomerIdentified, skuModelProduct, skuModelPricing, useNewMapPolicy, mapPolicy]);

  const basicClassName = classNames({
    'sui-text-right': align === 'end'
  });

  const signInSection = () => {
    return (
      useNewMapPolicy && mapPolicy === 'showSpecialPriceIfAuth' && !isCustomerIdentified && (
        <SignInLink
          layoutType="minimal"
          itemId={skuModelPricing?.itemId}
          align={align}
          isSignInToolTip={isSignInToolTip}
        />
      )
    );
  };

  const Basic = (price) => {
    return (
      <span className="sui-font-bold sui-text-lg" data-automation-id="pricingScenariosTotalPriceAddedText">
        {helpers.getUSDollarWithCommas(price)}
      </span>
    );
  };

  const BasicPrice = (price) => {
    return (
      <div className={basicClassName} data-automation-id="pricingScenariosText">
        {Basic(price)}
      </div>
    );
  };

  const BasicPerItemPrice = (price, unitPrice) => {
    return (
      <div className={basicClassName} data-automation-id="pricingScenariosText">
        {Basic(price)}
        <div>
          <span className="sui-text-subtle" data-automation-id="pricingScenariosPerItemText">
            {`(${helpers.getUSDollarWithCommas(unitPrice)}/item)`}
          </span>
        </div>
      </div>
    );
  };

  const StrikeThrough = (price) => {
    return (
      <span className="sui-line-through sui-text-lg sui-text-subtle sui-leading-none sui-mb-2">
        {helpers.getUSDollarWithCommas(price)}
      </span>
    );
  };

  const StrikeThroughPerItem = (original, discount) => {
    const formattedDiscount = helpers.getUSDollarWithCommas(discount) + '/item';
    const formattedOriginal = helpers.getUSDollarWithCommas(original);
    return (
      <Typography>
        ({formattedDiscount} <Typography component="span" decoration="line-through">{formattedOriginal}</Typography>)
      </Typography>
    );
  };

  const PercentWithMessage = (percent, message) => {
    let msg = message?.endsWith(' ') ? message : message + ' ';
    msg += percent + '%';
    return (
      // eslint-disable-next-line max-len
      <span className="sui-font-regular sui-text-base sui-leading-none sui-text-success" data-automation-id="pricingScenariosPercentOffText">
        {msg}
      </span>
    );
  };

  if (isBasicPerItemPrice(skuModel, quantity)) {
    const unitPrice = skuModelPricing.value
      ? skuModelPricing.value
      : skuModelPricing.original;
    return (
      <>
        {BasicPerItemPrice(totalPrice, unitPrice)}
        {signInSection()}
      </>
    );
  }

  if (isStrikeThroughPrice(skuModel, quantity)) {
    const unitPrice = skuModelPricing?.original ?? 0.00;
    const percentOffValue = skuModelPromotion?.percentageOff ?? 0;
    const message = skuModelPromotion?.message ?? 'no promotion available';
    const myStrikeThroughClass = classNames('sui-flex sui-flex-col', {
      'sui-text-right': align === 'end'
    });
    return (
      <>
        <div className={myStrikeThroughClass} data-automation-id="pricingScenariosText">
          {Basic(totalPrice)}
          {StrikeThrough(unitPrice)}
          {PercentWithMessage(percentOffValue, message)}
        </div>
        {signInSection()}
      </>
    );
  }

  if (isStrikeThroughPerItemPrice(skuModel, quantity)) {
    const originalUnitPrice = skuModelPricing?.original ?? 0.00;
    const discountUnitPrice = skuModelPricing?.value ?? 0.00;
    const percentOffValue = skuModelPromotion?.percentageOff ?? 0;
    const percentOffMessage = skuModelPromotion?.message ?? 'no promotion available';
    return (
      <>
        <div className={basicClassName} data-automation-id="pricingScenariosText">
          {Basic(totalPrice)}
          {StrikeThroughPerItem(originalUnitPrice, discountUnitPrice)}
          {PercentWithMessage(percentOffValue, percentOffMessage)}
        </div>
        {signInSection()}
      </>
    );
  }

  return BasicPrice(totalPrice);
};

PriceMinimal.propTypes = {
  /**
   * the transformed price data
   */
  skuModel: PropTypes.shape({
    _product: PropTypes.shape({
      pricing: PropTypes.shape({
        itemId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

        // unit price
        original: PropTypes.number.isRequired,

        // discount unit price
        value: PropTypes.number,

        // when true, display exchange pricing badge
        showExchangePricing: PropTypes.bool,

        // when true, display preferred pricing badge
        showPreferredPricingBadge: PropTypes.bool,

        // when true, display preferred pricing badge's tooltip
        showPreferredPricingBadgeToolTip: PropTypes.bool,

        cartItem: PropTypes.shape({

          // total price of this line-item
          total: PropTypes.number.isRequired,
        }),

        promotion: PropTypes.shape({
          // used to display %off message
          message: PropTypes.string,

          // % off when have discount
          percentageOff: PropTypes.string,
        }),
        dates: PropTypes.shape({
          // start date of discount
          start: PropTypes.string,

          // end date of discount
          end: PropTypes.string
        })
      }).isRequired
    }).isRequired,
  }).isRequired,
  quantity: PropTypes.number.isRequired,
  align: PropTypes.string,
  useNewMapPolicy: PropTypes.bool,
  isSignInToolTip: PropTypes.bool
};

PriceMinimal.displayName = 'PriceMinimal';
